import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import StudentProfiles from './pages/StudentProfiles';
import InterviewContext from './pages/InterviewContext';
import ProfileDetail from './pages/ProfileDetail';
import StudentView from './pages/StudentView';
import EcommerceLanding from './pages/EcommerceLanding';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [interviewContext, setInterviewContext] = useState(null);
  const inactivityTimerRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);
  const deploymentCheckIntervalRef = useRef(null);
  const deploymentVersionRef = useRef(null);
  const API_BASE = process.env.NODE_ENV === 'production' 
    ? window.location.origin 
    : 'http://localhost:5001';

  // Initialize user from localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('userEmail');
    if (token && email) {
      setUser(email);
    }
    setLoading(false);
  }, []);

  // Check session validity periodically
  useEffect(() => {
    if (!user) return;

    const checkSessionValidity = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await axios.get(`${API_BASE}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.data.valid) {
          // Session expired - logged in elsewhere
          handleSessionExpired();
        }
      } catch (err) {
        if (err.response?.data?.sessionExpired) {
          handleSessionExpired();
        }
      }
    };

    // Check session every 30 seconds
    sessionCheckIntervalRef.current = setInterval(checkSessionValidity, 30000);
    
    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
    };
  }, [user]);

  // Check for deployment changes (auto-logout on deployment)
  useEffect(() => {
    if (!user) return;

    const checkDeploymentVersion = async () => {
      try {
        const response = await axios.get(`${API_BASE}/deployment-version`);
        const currentVersion = response.data.version;

        // Store initial version on first check
        if (deploymentVersionRef.current === null) {
          deploymentVersionRef.current = currentVersion;
          console.log('✓ Deployment version recorded:', currentVersion);
          return;
        }

        // If version changed, logout user
        if (deploymentVersionRef.current !== currentVersion) {
          console.warn('⚠ Deployment detected! Version changed from', deploymentVersionRef.current, 'to', currentVersion);
          handleDeploymentLogout();
        }
      } catch (err) {
        console.error('Error checking deployment version:', err.message);
      }
    };

    // Check deployment version every 60 seconds
    deploymentCheckIntervalRef.current = setInterval(checkDeploymentVersion, 60000);

    // Check immediately on mount
    checkDeploymentVersion();
    
    return () => {
      if (deploymentCheckIntervalRef.current) {
        clearInterval(deploymentCheckIntervalRef.current);
      }
    };
  }, [user]);

  // Inactivity timer for 90 seconds
  const resetInactivityTimer = () => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer for 90 seconds only when user is logged in
    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        console.log('User inactive for 90 seconds - showing ecom view');
        // Trigger inactivity by setting a flag in localStorage
        localStorage.setItem('userInactive', 'true');
        // Force a re-render in Dashboard to show ecom view
        window.dispatchEvent(new Event('userInactivity'));
      }, 90000); // 90 seconds
    }
  };

  // Listen for user activity
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      localStorage.removeItem('userInactive');
      resetInactivityTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initial timer
    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user]);

  const handleSessionExpired = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userInactive');
    setUser(null);
    alert('Your session has expired. You were logged in from another browser.');
  };

  const handleDeploymentLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userInactive');
    localStorage.removeItem('activeSessionId');
    localStorage.removeItem('activeShareUrl');
    if (deploymentCheckIntervalRef.current) {
      clearInterval(deploymentCheckIntervalRef.current);
    }
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
    }
    setUser(null);
    setSelectedProfile(null);
    setInterviewContext(null);
    alert('Application has been updated. Please login again.');
  };

  const handleLogin = (email) => {
    setUser(email);
    setSelectedProfile(null);
    setInterviewContext(null);
    localStorage.removeItem('userInactive');
    resetInactivityTimer();
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call backend logout endpoint to end sessions
        await axios.post(
          `${API_BASE_URL}/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(err => {
          // Log error but don't block logout
          console.error('Logout API error:', err.message);
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userInactive');
      localStorage.removeItem('activeSessionId');
      localStorage.removeItem('activeShareUrl');
      setUser(null);
      setSelectedProfile(null);
      setInterviewContext(null);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (deploymentCheckIntervalRef.current) {
        clearInterval(deploymentCheckIntervalRef.current);
      }
    }
  };

  const handleProfileSelect = (profileId, profileData) => {
    setSelectedProfile({ id: profileId, ...profileData });
    setInterviewContext(null);
  };

  const handleInterviewContextSet = (profileId, profileData, companyName, interviewRound) => {
    setSelectedProfile({ id: profileId, ...profileData });
    setInterviewContext({ companyName, interviewRound });
  };

  const handleBackToProfiles = () => {
    setSelectedProfile(null);
    setInterviewContext(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/profiles" />}
        />
        <Route
          path="/profiles"
          element={user ? <StudentProfiles user={user} onLogout={handleLogout} onProfileSelect={handleProfileSelect} /> : <Navigate to="/login" />}
        />
        <Route
          path="/profile/:id"
          element={user && selectedProfile ? <ProfileDetail profile={selectedProfile} onLogout={handleLogout} onInterviewSelect={handleInterviewContextSet} onBack={handleBackToProfiles} /> : <Navigate to="/profiles" />}
        />
        <Route
          path="/interview"
          element={user && selectedProfile ? <InterviewContext profile={selectedProfile} onContinue={handleInterviewContextSet} onBack={() => setSelectedProfile(null)} /> : <Navigate to="/profiles" />}
        />
        <Route
          path="/search"
          element={user ? <Dashboard user={user} onLogout={handleLogout} profile={selectedProfile} interviewContext={interviewContext} /> : <Navigate to={user ? '/profiles' : '/login'} />}
        />
        <Route
          path="/session/:sessionId"
          element={<StudentView />}
        />
        <Route
          path="/home"
          element={<EcommerceLanding />}
        />
        <Route path="/" element={<Navigate to={user ? (interviewContext ? '/search' : selectedProfile ? `/profile/${selectedProfile.id}` : '/profiles') : '/login'} />} />
      </Routes>
    </Router>
  );
}

export default App;
